import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, FileUp, Loader2, Search, Trash2, Link2, CheckCircle2, HelpCircle, AlertCircle, Package, Upload } from "lucide-react";
import { marginAnalysisAPI, clientAPI } from "@/lib/api";
import { toast } from "sonner";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const MONTH_NAMES = { "01": "Jan", "02": "Fév", "03": "Mar", "04": "Avr", "05": "Mai", "06": "Juin", "07": "Juil", "08": "Août", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Déc" };

function MarginBadge({ value, size = "sm" }) {
  if (value === 0 || value === undefined || value === null) return <span className="text-slate-300">-</span>;
  const isPositive = value > 0;
  const sizeClass = size === "lg" ? "text-lg" : "text-sm";
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-semibold ${sizeClass} ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
      {isPositive ? <TrendingUp size={size === "lg" ? 16 : 13} /> : <TrendingDown size={size === "lg" ? 16 : 13} />}
      {value > 0 ? "+" : ""}{value.toFixed(1)}%
    </span>
  );
}

function MarginBar({ cost, revenue }) {
  if (!cost && !revenue) return null;
  const max = Math.max(cost, revenue);
  const costW = max > 0 ? (cost / max) * 100 : 0;
  const revW = max > 0 ? (revenue / max) * 100 : 0;
  return (
    <div className="flex flex-col gap-0.5 w-32">
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-red-400 rounded-full" style={{ width: `${costW}%` }} />
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${revW}%` }} />
      </div>
    </div>
  );
}

/* ===================== OVERVIEW TAB ===================== */
function OverviewTab({ year, onYearChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [pricing, setPricing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, pricingRes] = await Promise.all([
        marginAnalysisAPI.overview(year),
        marginAnalysisAPI.pricing(20),
      ]);
      setData(overviewRes.data);
      setPricing(pricingRes.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (name) => setExpanded(p => ({ ...p, [name]: !p[name] }));

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { totals, suppliers } = data;

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-slate-200">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Coût fournisseurs</p>
          <p className="text-xl font-bold font-mono text-slate-900" data-testid="overview-total-cost">{formatEur(totals.cost_ht)}</p>
        </Card>
        <Card className="p-4 border-slate-200">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Facturé clients</p>
          <p className="text-xl font-bold font-mono text-slate-900" data-testid="overview-total-revenue">{formatEur(totals.revenue_ht)}</p>
        </Card>
        <Card className={`p-4 ${totals.margin_eur >= 0 ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30"}`}>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Marge totale</p>
          <p className={`text-xl font-bold font-mono ${totals.margin_eur >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="overview-total-margin">{formatEur(totals.margin_eur)}</p>
        </Card>
        <Card className="p-4 border-slate-200">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Marge %</p>
          <div className="mt-1" data-testid="overview-margin-pct"><MarginBadge value={totals.margin_pct} size="lg" /></div>
        </Card>
      </div>

      {/* Supplier table */}
      {suppliers.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Package size={40} className="mx-auto text-slate-300 mb-3" strokeWidth={1} />
          <p className="text-slate-500">Aucune facture fournisseur pour {year}</p>
        </Card>
      ) : (
        <div className="responsive-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="uppercase text-xs text-slate-500 w-8"></TableHead>
              <TableHead className="uppercase text-xs text-slate-500">Fournisseur / Produit</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-center">Fact. fourn.</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-center">Fact. clients</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-center">Produits</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-right">Coût HT</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-right">Facturé HT</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-center"></TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-right">Marge</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-center">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suppliers.map((s, idx) => {
                const rowKey = `${s.supplier_name}|${s.product}`;
                const isExpanded = expanded[rowKey];
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow className="table-row-hover cursor-pointer" onClick={() => toggleExpand(rowKey)} data-testid={`supplier-row-${idx}`}>
                      <TableCell>
                        <button className="p-0.5 rounded hover:bg-slate-200">
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-1.5">
                          {s.supplier_name}
                          {s.estimated && <Badge variant="outline" className="text-[9px] text-amber-500 border-amber-300 px-1">estimé</Badge>}
                        </div>
                        <div className="text-xs text-slate-500 font-normal">{s.product}</div>
                      </TableCell>
                      <TableCell className="text-center"><Badge variant="outline" className="text-xs font-mono">{s.supplier_invoice_count}</Badge></TableCell>
                      <TableCell className="text-center"><Badge variant="outline" className="text-xs font-mono">{s.client_invoice_count}</Badge></TableCell>
                      <TableCell className="text-center text-xs text-slate-500">{s.matched_products}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-red-600">{formatEur(s.total_cost_ht)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-emerald-600">{formatEur(s.total_revenue_ht)}</TableCell>
                      <TableCell><MarginBar cost={s.total_cost_ht} revenue={s.total_revenue_ht} /></TableCell>
                      <TableCell className={`text-right font-mono text-sm font-bold ${s.margin_eur >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatEur(s.margin_eur)}</TableCell>
                      <TableCell className="text-center"><MarginBadge value={s.margin_pct} /></TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="bg-slate-50/80">
                        <TableCell colSpan={10} className="p-0">
                          <div className="px-8 py-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Détail mensuel — {s.supplier_name} / {s.product}</p>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-200">
                                  <th className="text-left py-1.5 pr-4">Mois</th>
                                  <th className="text-right py-1.5 pr-4">Coût HT</th>
                                  <th className="text-right py-1.5 pr-4">Facturé HT</th>
                                  <th className="text-right py-1.5 pr-4">Marge</th>
                                  <th className="text-center py-1.5">%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(s.monthly_detail || []).map(m => (
                                  <tr key={m.month} className="border-t border-slate-100">
                                    <td className="py-1.5 pr-4 font-medium">{MONTH_NAMES[m.month?.slice(5)] || m.month} {m.month?.slice(0, 4)}</td>
                                    <td className="py-1.5 pr-4 text-right font-mono text-red-600">{formatEur(m.cost_ht)}</td>
                                    <td className="py-1.5 pr-4 text-right font-mono text-emerald-600">{formatEur(m.revenue_ht)}</td>
                                    <td className={`py-1.5 pr-4 text-right font-mono font-semibold ${m.margin_eur >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatEur(m.margin_eur)}</td>
                                    <td className="py-1.5 text-center"><MarginBadge value={m.margin_pct} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pricing recommendations */}
      {pricing && pricing.recommendations && pricing.recommendations.length > 0 && (
        <Card className="p-6 border-slate-200 mt-6">
          <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Alignement sur les prix publics Google</h3>
          <p className="text-xs text-slate-400 mb-4">Comparaison prix revendeur / prix public mensuel / votre tarif actuel {pricing.csv_period ? `(coûts CSV ${MONTH_NAMES[pricing.csv_period?.slice(5)] || ""} ${pricing.csv_period?.slice(0, 4) || ""})` : ""}</p>
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="uppercase text-xs text-slate-500">Produit</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">Votre coût/mois</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">Prix public/mois</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-center">Marge au public</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">Votre prix/an</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">Prix public/an</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-center">Marge actuelle</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">Ecart/an</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricing.recommendations.map((r, i) => (
                  <TableRow key={r.reference || r.product || `price-${i}`} data-testid={`pricing-row-${i}`}>
                    <TableCell className="font-medium">{r.product} <span className="text-xs text-slate-400">{r.reference}</span></TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatEur(r.cost_per_seat_month)}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold text-indigo-700">{formatEur(r.public_price_month)}</TableCell>
                    <TableCell className="text-center"><MarginBadge value={r.margin_at_public_pct} /></TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatEur(r.current_price_year)}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold text-indigo-700">{formatEur(r.public_price_year)}</TableCell>
                    <TableCell className="text-center"><MarginBadge value={r.current_margin_pct} /></TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        if (r.gap_year > 0) return <span className="text-sm font-mono font-semibold text-red-600">+{formatEur(r.gap_year)}</span>;
                        if (r.gap_year < 0) return <span className="text-sm font-mono font-semibold text-emerald-600">{formatEur(r.gap_year)}</span>;
                        return <span className="text-sm text-slate-400">-</span>;
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Prix publics Google Workspace mensuels flexibles France 2025 (source: workspace.google.fr). En vous calant sur ces prix, la marge correspond au discount revendeur Google.</p>
        </Card>
      )}
    </div>
  );
}

/* ===================== CSV DETAIL TAB ===================== */
function MatchStatusIcon({ type }) {
  if (type === "auto") return <CheckCircle2 size={14} className="text-emerald-500" />;
  if (type === "manual") return <Link2 size={14} className="text-blue-500" />;
  return <HelpCircle size={14} className="text-amber-400" />;
}

function ClientMatchDialog({ open, onOpenChange, domain, onMatch }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); }
    else if (domain) setQuery(domain.split(".")[0]);
  }, [open, domain]);

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await clientAPI.list({ search: query.trim(), limit: 10 });
        setResults(res.data.items || res.data || []);
      } catch (e) { toast.error("Erreur recherche client"); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Associer un client</DialogTitle>
          <DialogDescription>Domaine : <strong>{domain}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher un client..." value={query} onChange={e => setQuery(e.target.value)} className="pl-9" autoFocus data-testid="match-client-search" />
          </div>
          {searching && <div className="text-center py-3 text-slate-400"><Loader2 size={16} className="animate-spin inline mr-2" />Recherche...</div>}
          <div className="max-h-60 overflow-y-auto space-y-1">
            {results.map(c => (
              <button key={c.id} onClick={() => { onMatch(c.id, c.company || c.nom_etablissement || ""); onOpenChange(false); }}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors"
                data-testid={`match-client-${c.id}`}>
                <p className="text-sm font-medium">{c.company || c.nom_etablissement}</p>
                <p className="text-xs text-slate-400">{c.email || (c.emails || [])[0] || ""}</p>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CsvDetailTab() {
  const [analyses, setAnalyses] = useState([]);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragover, setDragover] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [matchDomain, setMatchDomain] = useState(null);
  const fileRef = useRef(null);

  const loadAnalyses = useCallback(() => {
    marginAnalysisAPI.list().then(r => setAnalyses(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setParsedResult(null);
    setCurrentAnalysis(null);
    try {
      const res = await marginAnalysisAPI.parse(file);
      setParsedResult(res.data);
      toast.success(`${res.data.total_lines} lignes, ${res.data.matched} clients matchés`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!parsedResult) return;
    setSaving(true);
    try {
      const res = await marginAnalysisAPI.save({
        period: parsedResult.period,
        source: "Google Workspace",
        original_filename: parsedResult.original_filename,
        lines: parsedResult.lines,
        total_cost: parsedResult.total_cost,
      });
      toast.success("Analyse sauvegardée");
      setCurrentAnalysis(res.data);
      setParsedResult(null);
      loadAnalyses();
    } catch { toast.error("Erreur sauvegarde"); }
    finally { setSaving(false); }
  };

  const openAnalysis = async (id) => {
    try {
      const res = await marginAnalysisAPI.get(id);
      setCurrentAnalysis(res.data);
      setParsedResult(null);
    } catch { toast.error("Erreur chargement"); }
  };

  const deleteAnalysis = async (id) => {
    if (!window.confirm("Supprimer cette analyse ?")) return;
    try {
      await marginAnalysisAPI.delete(id);
      if (currentAnalysis?.id === id) setCurrentAnalysis(null);
      loadAnalyses();
    } catch { toast.error("Erreur"); }
  };

  const handleMatch = async (clientId, clientName) => {
    if (!currentAnalysis?.id || !matchDomain) return;
    try {
      await marginAnalysisAPI.updateMatch(currentAnalysis.id, { domain: matchDomain, client_id: clientId, client_name: clientName });
      toast.success(`${matchDomain} associé à ${clientName}`);
      openAnalysis(currentAnalysis.id);
    } catch { toast.error("Erreur association"); }
  };

  const displayData = currentAnalysis || parsedResult;
  const lines = displayData?.lines || [];
  const PERIOD_MONTHS = { "01": "Janvier", "02": "Février", "03": "Mars", "04": "Avril", "05": "Mai", "06": "Juin", "07": "Juillet", "08": "Août", "09": "Septembre", "10": "Octobre", "11": "Novembre", "12": "Décembre" };
  const formatPeriod = (p) => { if (!p) return "-"; const [y, m] = p.split("-"); return `${PERIOD_MONTHS[m] || m} ${y}`; };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left: upload + history */}
      <div className="lg:col-span-1 space-y-4">
        <Card className="p-4 border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Importer CSV Google</h3>
          <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragover ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-400"}`}
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={e => { e.preventDefault(); setDragover(false); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            data-testid="margin-upload-zone">
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ""; }} />
            {uploading ? <Loader2 size={28} className="text-indigo-600 animate-spin mx-auto" /> :
              <><FileUp size={28} className="text-slate-400 mx-auto" strokeWidth={1.5} /><p className="text-xs text-slate-500 mt-2">CSV Google Partner</p></>}
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Historique CSV</h3>
          {analyses.length === 0 ? <p className="text-xs text-slate-400 text-center py-3">Aucune analyse</p> :
            <div className="space-y-2">{analyses.map(a => (
              <div key={a.id} className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-colors ${currentAnalysis?.id === a.id ? "border-indigo-300 bg-indigo-50" : "border-slate-100 hover:border-slate-300"}`}
                onClick={() => openAnalysis(a.id)} data-testid={`analysis-${a.id}`}>
                <div>
                  <p className="text-sm font-medium">{formatPeriod(a.period)}</p>
                  <p className="text-[10px] text-slate-400">{(a.lines || []).length} lignes</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0"
                  onClick={e => { e.stopPropagation(); deleteAnalysis(a.id); }}><Trash2 size={13} /></Button>
              </div>
            ))}</div>}
        </Card>
      </div>

      {/* Right: results */}
      <div className="lg:col-span-3">
        {displayData ? (
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatPeriod(displayData.period)}</h2>
                <p className="text-sm text-slate-500">{lines.length} clients · {lines.filter(l => l.client_id).length} associés
                  {lines.filter(l => !l.client_id).length > 0 && <span className="text-amber-500 ml-2"><AlertCircle size={13} className="inline mr-0.5" />{lines.filter(l => !l.client_id).length} non associé(s)</span>}
                </p>
              </div>
              {parsedResult && !currentAnalysis && (
                <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-analysis-btn">
                  {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}Sauvegarder
                </Button>
              )}
            </div>
            <div className="responsive-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="uppercase text-xs text-slate-500 w-8"></TableHead>
                    <TableHead className="uppercase text-xs text-slate-500">Client</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500">Domaine</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500">Produit</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500 text-center">Seats</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500 text-right">Coût/mois</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500 text-right">Coût/an</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500 text-right">Facturé/an</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500 text-right">Marge</TableHead>
                    <TableHead className="uppercase text-xs text-slate-500 text-center">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, idx) => {
                    const hasInvoices = (line.invoices || []).length > 0;
                    return (
                      <React.Fragment key={line.product_name || `margin-${idx}`}>
                        <TableRow className={`table-row-hover ${!line.client_id ? "bg-amber-50/50" : ""}`} data-testid={`margin-line-${idx}`}>
                          <TableCell>
                            {hasInvoices && <button onClick={() => setExpanded(p => ({ ...p, [idx]: !p[idx] }))} className="p-0.5 rounded hover:bg-slate-200">{expanded[idx] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <MatchStatusIcon type={line.match_type} />
                              {line.client_id ? <span className="text-sm font-medium">{line.client_name}</span> :
                                <button onClick={() => setMatchDomain(line.domain)} className="text-sm text-amber-600 hover:underline flex items-center gap-1" data-testid={`match-btn-${idx}`}><Link2 size={12} /> Associer</button>}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 font-mono">{line.domain}</TableCell>
                          <TableCell className="text-sm">{line.product?.replace("Google Workspace ", "")}</TableCell>
                          <TableCell className="text-center font-mono text-sm">{line.seats}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatEur(line.cost_ht)}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">{formatEur(line.annual_cost)}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">{line.annual_revenue > 0 ? formatEur(line.annual_revenue) : <span className="text-slate-300">-</span>}</TableCell>
                          <TableCell className={`text-right font-mono text-sm font-bold ${(() => { if (line.margin_eur > 0) return "text-emerald-600"; if (line.margin_eur < 0) return "text-red-600"; return "text-slate-400"; })()}`}>
                            {line.client_id ? formatEur(line.margin_eur) : "-"}
                          </TableCell>
                          <TableCell className="text-center"><MarginBadge value={line.margin_pct} /></TableCell>
                        </TableRow>
                        {expanded[idx] && hasInvoices && (
                          <TableRow className="bg-slate-50/80">
                            <TableCell colSpan={10} className="p-0">
                              <div className="px-8 py-3">
                                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Factures clients</p>
                                <table className="w-full text-sm">
                                  <thead><tr className="text-[10px] uppercase text-slate-400">
                                    <th className="text-left py-1 pr-4">N°</th><th className="text-left py-1 pr-4">Date</th>
                                    <th className="text-left py-1 pr-4">Produit</th><th className="text-center py-1 pr-4">Qté</th>
                                    <th className="text-right py-1 pr-4">PU HT</th><th className="text-right py-1">Total HT</th>
                                  </tr></thead>
                                  <tbody>{(line.invoices || []).map((inv) => (
                                    <tr key={inv.number || inv.date} className="border-t border-slate-100">
                                      <td className="py-1.5 pr-4 font-mono text-indigo-600">{inv.number}</td>
                                      <td className="py-1.5 pr-4">{inv.date ? new Date(inv.date).toLocaleDateString("fr-FR") : "-"}</td>
                                      <td className="py-1.5 pr-4">{inv.product}</td>
                                      <td className="py-1.5 pr-4 text-center font-mono">{inv.quantity}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono">{formatEur(inv.unit_price)}</td>
                                      <td className="py-1.5 text-right font-mono font-semibold">{formatEur(inv.total_ht)}</td>
                                    </tr>
                                  ))}</tbody>
                                </table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : (
          <Card className="p-12 border-slate-200 text-center">
            <Upload size={40} className="mx-auto text-slate-300 mb-3" strokeWidth={1} />
            <p className="text-slate-500">Importez un CSV Google Partner pour le détail par client</p>
          </Card>
        )}
      </div>

      <ClientMatchDialog open={!!matchDomain} onOpenChange={v => { if (!v) setMatchDomain(null); }} domain={matchDomain || ""} onMatch={handleMatch} />
    </div>
  );
}

/* ===================== MAIN PAGE ===================== */
export default function MarginAnalysisPage() {
  const currentYear = new Date().getFullYear().toString();
  const [year, setYear] = useState("2025");
  const years = [];
  for (let y = parseInt(currentYear); y >= 2020; y--) years.push(y.toString());

  return (
    <div className="page-container" data-testid="margin-analysis-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Marge produit</h1>
          <p className="text-slate-500 mt-1">Coûts fournisseurs vs revenus clients</p>
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28" data-testid="year-selector"><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="csv" data-testid="tab-csv">Détail Google (CSV)</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <OverviewTab year={year} onYearChange={setYear} />
        </TabsContent>
        <TabsContent value="csv">
          <CsvDetailTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
